// Shared email validation for EVERY guest/customer/agent email capture point
// (customer checkout, staff new-booking + edit, agent portal, vouchers,
// membership, manage-booking self-service, OTA inbound). One validator so the
// same address is accepted/rejected/normalised identically everywhere — client
// for UX, server as the authority.
//
// Why this exists: malformed addresses (`onwards.austin`, `hotmail,.om`,
// `outlook.com.`, `yahoo.com,hk`) were persisted at booking because there was NO
// real validation on the write path — the browser's native `type="email"` check
// only fires on a native form submit, and checkout submits via a click handler,
// so it never ran. Postmark then rejected the address (ErrorCode 300) and the
// guest got no confirmation. See card c1be1f7d.
//
// Deliberately NOT full RFC 5322 — the goal is catching typos, not rejecting
// exotic-but-valid addresses. It accepts legitimately awkward ones like
// name+tag@sub.domain.co.nz, o'brien@example.com, a.b.c@example.museum.

export const EMAIL_ERROR_MESSAGE = "Enter a valid email address, e.g. sophie@example.com";

// A domain label (between dots): anything that isn't whitespace, "@", comma or a
// dot. Empty labels (a leading/trailing/consecutive dot in the domain) fail this.
const DOMAIN_LABEL = /^[^\s@,.]+$/;
// Local part: no whitespace, "@" or comma. Leading/trailing/consecutive dots are
// checked separately. Everything else (+, ', %, -, _, …) is allowed.
const LOCAL_PART = /^[^\s@,]+$/;
// Last domain label must look like a real TLD: ≥2 ASCII letters (rejects
// `foo@bar.c`, `foo@bar.123`, and — with the no-dot check — `foo@bar`).
const TLD = /^[A-Za-z]{2,}$/;

/**
 * Canonicalise an address before validating/persisting: trim, strip a SINGLE
 * trailing dot (a pure typo — "name@gmail.com." → "name@gmail.com"), lowercase.
 * Returns "" for null/blank. This is what recovers three of the four real
 * failures (the trailing-dot ones) into valid, sendable addresses.
 */
export function normalizeEmail(raw: string | null | undefined): string {
  let e = (raw ?? "").trim().toLowerCase();
  if (e.endsWith(".")) e = e.slice(0, -1).trimEnd();
  return e;
}

/**
 * Validate (after normalising). Returns the normalised address to persist on
 * success, or a human-readable reason on failure. Rejects: empty, no "@", more
 * than one "@", a comma anywhere, a leading/trailing dot in either part,
 * consecutive dots, no dot in the domain, an empty domain label, and a domain
 * whose last label isn't a plausible TLD.
 */
export function validateEmail(
  raw: string | null | undefined,
): { ok: true; email: string } | { ok: false; error: string } {
  const email = normalizeEmail(raw);
  const fail = { ok: false as const, error: EMAIL_ERROR_MESSAGE };

  if (!email) return fail;
  if (email.includes(",")) return fail;          // comma anywhere
  if (email.includes("..")) return fail;         // consecutive dots (either part)

  const at = email.indexOf("@");
  if (at <= 0) return fail;                       // no "@", or empty local part
  if (at !== email.lastIndexOf("@")) return fail; // more than one "@"

  const local = email.slice(0, at);
  const domain = email.slice(at + 1);
  if (!LOCAL_PART.test(local)) return fail;
  if (local.startsWith(".") || local.endsWith(".")) return fail;

  if (!domain || domain.startsWith(".") || domain.endsWith(".")) return fail;
  const labels = domain.split(".");
  if (labels.length < 2) return fail;            // no dot in the domain
  if (labels.some((l) => !DOMAIN_LABEL.test(l))) return fail;
  if (!TLD.test(labels[labels.length - 1])) return fail;

  return { ok: true, email };
}

/** Convenience boolean wrapper (client-side field checks, filters). */
export function isValidEmail(raw: string | null | undefined): boolean {
  return validateEmail(raw).ok;
}

// Common consumer-domain fat-fingers → the address the guest almost certainly
// meant. Used ONLY for a soft, non-blocking "did you mean…?" hint — never to
// auto-correct or block. Deliberately a small explicit map (no fuzzy matching)
// so it can only ever fire on a known typo.
const DOMAIN_TYPOS: Record<string, string> = {
  "gmial.com": "gmail.com", "gmai.com": "gmail.com", "gmail.co": "gmail.com",
  "gmail.con": "gmail.com", "gmail.cm": "gmail.com", "gnail.com": "gmail.com",
  "gamil.com": "gmail.com",
  "hotmial.com": "hotmail.com", "hotmal.com": "hotmail.com", "hotmai.com": "hotmail.com",
  "hotmail.con": "hotmail.com", "hotmail.co": "hotmail.com", "hotmial.co.nz": "hotmail.co.nz",
  "outlook.con": "outlook.com", "outloo.com": "outlook.com", "outlok.com": "outlook.com",
  "outllok.com": "outlook.com",
  "yaho.com": "yahoo.com", "yahoo.con": "yahoo.com", "yahooo.com": "yahoo.com",
  "iclould.com": "icloud.com", "icloud.con": "icloud.com", "icloud.co": "icloud.com",
  "live.con": "live.com", "xtra.co": "xtra.co.nz",
};

/**
 * If the domain is a known typo of a common provider, return the corrected
 * address to suggest (e.g. "sophie@gmial.com" → "sophie@gmail.com"); otherwise
 * null. Advisory only — the caller still lets the guest proceed.
 */
export function suggestEmailDomain(raw: string | null | undefined): string | null {
  const email = normalizeEmail(raw);
  const at = email.lastIndexOf("@");
  if (at <= 0) return null;
  const local = email.slice(0, at);
  const domain = email.slice(at + 1);
  const fixed = DOMAIN_TYPOS[domain];
  return fixed && fixed !== domain ? `${local}@${fixed}` : null;
}
